import type { TextFieldProps } from '@mui/material/TextField';
import { InputAdornment, TextField, Theme } from '@mui/material';
import { SxProps } from '@mui/system';
import React, { ChangeEvent, ReactNode, forwardRef, useCallback } from 'react';

export type TextInputProps = Pick<
  TextFieldProps,
  'id' | 'name' | 'type' | 'onBlur' | 'onFocus' | 'autoComplete' | 'inputProps' | 'InputProps'
> & {
  label?: ReactNode;
  placeholder?: string;
  value: string | number | null | undefined;
  onChange(e: ChangeEvent<HTMLInputElement>, value: string): void;
  error?: boolean;
  helperText?: ReactNode;
  required?: boolean;
  disabled?: boolean;
  multiline?: boolean;
  rows?: number;
  minRows?: number;
  maxRows?: number;
  /**
   * Keeps the label floated up, which is what lets the placeholder show. An outlined field with a label
   * hides its placeholder until the label shrinks — that is, until focus or a value — so a placeholder
   * meant to be read before either is invisible without this.
   */
  shrinkLabel?: boolean;
  endIcon?: ReactNode;
  size?: TextFieldProps['size'];
  variant?: "outlined" | "filled" | "standard";
  margin?: "dense" | "normal" | "none";
  fullWidth?: boolean;
  sx?: SxProps<Theme>;
};

export const TextInput = forwardRef<HTMLInputElement, TextInputProps>((props, ref) => {
  const {
    label,
    placeholder,
    value,
    onChange,
    error,
    helperText,
    required,
    disabled,
    multiline,
    rows,
    minRows,
    maxRows,
    shrinkLabel,
    endIcon,
    size,
    variant = "outlined",
    margin = "normal",
    fullWidth = true,
    sx,
    InputProps,
    ...inputProps
  } = props;

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      onChange(e, value);
    },
    [onChange]
  );

  const inputSx = InputProps?.sx;

  return (
    <TextField
      {...inputProps}
      inputRef={ref}
      label={label}
      placeholder={placeholder}
      value={value || ""}
      onChange={handleChange}
      error={error}
      helperText={helperText}
      multiline={multiline}
      rows={rows}
      minRows={minRows}
      maxRows={maxRows}
      InputLabelProps={shrinkLabel ? { shrink: true } : undefined}
      required={required}
      disabled={disabled}
      size={size}
      variant={variant}
      margin={margin}
      fullWidth={fullWidth}
      InputProps={{
        ...InputProps,
        endAdornment: endIcon
          ? <InputAdornment position="end">{endIcon}</InputAdornment>
          : InputProps?.endAdornment,
        // sx lands on the input, not the FormControl, so callers can size the box itself.
        sx: [
          ...(Array.isArray(inputSx) ? inputSx : [inputSx]),
          ...(Array.isArray(sx) ? sx : [sx]),
        ],
      }}
    />
  );
});

TextInput.displayName = 'TextInput';
